// client/src/components/login/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ResetPasswordPage.css'; // Import the CSS
import { generateSecurePassword } from '../../utils/passwordGenerator'; // Import
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // Import icons

function ResetPasswordPage() {
    const { t } = useTranslation();
    const { token } = useParams(); // Get the token from the URL parameter
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Loading indicator
    const [passwordStrength, setPasswordStrength] = useState(''); // Add strength
    const [showPassword, setShowPassword] = useState(false); // Show/hide

    useEffect(() => {
        if (!token) {
            navigate('/login'); // Redirect if no token.  Or show an error.
        }
        // You *could* pre-validate the token format here (client-side),
        // but the server MUST validate it anyway.
    }, [token, navigate]);

    const handlePasswordChange = (event) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        setPasswordError(''); // Clear error on change
        setPasswordStrength(calculatePasswordStrength(newPassword)); // Update strength
    };

    const handleConfirmPasswordChange = (event) => {
        setConfirmPassword(event.target.value);
        setConfirmPasswordError(''); // Clear error
    };

    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        setPassword(newPassword);
        setConfirmPassword(newPassword); // Set confirm too!
        setPasswordError('');
        setPasswordStrength(calculatePasswordStrength(newPassword));
        setShowPassword(true); // Show the generated password. IMPORTANT
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const calculatePasswordStrength = (password) => {
        if (password.length === 0) {
            return '';
        }
        if (password.length < 8) {
            return 'weak';
        }
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++; // Uppercase
        if (/[a-z]/.test(password)) score++; // Lowercase
        if (/[0-9]/.test(password)) score++; // Digit
        if (/[^A-Za-z0-9]/.test(password)) score++; // Special character

        if (score <= 2) return 'weak';
        if (score <= 4) return 'medium';
        return 'strong';
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        setPasswordError('');
        setConfirmPasswordError('');
        setError('');
        setMessage('');
        setIsLoading(true);

        if (!password) {
            setPasswordError(t('Register.errors.passwordRequired')); // Reuse translations
            setIsLoading(false);
            return;
        }
        if (password.length < 8) {
            setPasswordError(t('Register.errors.passwordTooShort'));
            setIsLoading(false);
            return;
        }
        if (password !== confirmPassword) {
            setConfirmPasswordError(t('Register.errors.passwordsDontMatch'));
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/auth/reset-password', { // NEW ENDPOINT
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,  // Send the token
                    password,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setMessage(data.message); // Display success message from server.
                setError('');
                setTimeout(() => {
                    navigate('/login');
                }, 3000); // Redirect after a delay

            } else {
                const data = await response.json(); // Parse error response
                setError(data.message || 'An unexpected error occurred.');
                setMessage('');
            }
        } catch (err) {
            console.error("Error resetting password:", err);
            setError('An unexpected error occurred.');
            setMessage('');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="reset-password-page">
            <div className="reset-password-content">
                <h2>{t('resetPassword.title')}</h2>
                <p>{t('resetPassword.instructions')}</p>

                <form onSubmit={handleSubmit} className="reset-password-form">
                    <div className="register-form__group">
                        <label htmlFor="password" className="register-form__label">{t('Register.passwordLabel')}</label>
                        <div className="register-form__password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'} // Toggle type
                                id="password"
                                name="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder={t('Register.passwordPlaceholder')}
                                className={`reset-password-form__input ${passwordError ? 'reset-password-form__input--error' : ''}`}
                            />
                            {/* Show/Hide Toggle */}
                            <button
                                type="button"
                                className="register-form__toggle-password-button"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? t('Register.hidePassword') : t('Register.showPassword')}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            {/* Generate Button */}
                            <button
                                type="button"
                                className="register-form__generate-password-button"
                                onClick={handleGeneratePassword}
                                aria-label={t('Register.generatePassword')}
                            >
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                        {passwordError && <div className="reset-password-form__error">{passwordError}</div>}
                        {/* Password Strength */}
                        {password && (
                            <div className="register-form__password-strength">
                                <div className={`password-strength-bar password-strength-${passwordStrength}`}></div>
                            </div>
                        )}
                    </div>

                    <div className="register-form__group">
                        <label htmlFor="confirmPassword" className="register-form__label">{t('Register.confirmPasswordLabel')}</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            placeholder={t('Register.confirmPasswordPlaceholder')}
                            className={`reset-password-form__input ${confirmPasswordError ? 'reset-password-form__input--error' : ''}`}
                        />
                        {confirmPasswordError && <div className="reset-password-form__error">{confirmPasswordError}</div>}
                    </div>

                    {error && <div className="reset-password-form__error">{error}</div>}
                    {message && <div className="reset-password-form__message">{message}</div>}

                    <button type="submit" className="reset-password-form__button" disabled={isLoading}>
                        {isLoading ? t('resetPassword.resetting') : t('resetPassword.resetPasswordButton')}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ResetPasswordPage;